import { make } from "@/editor/utils/dom";
import { API, BlockAPI, PasteEvent } from "@editorjs/editorjs";
import ImageTool from "@editorjs/image";
import "./index.css";

class CustomImageTool {
  private imageTool: ImageTool;
  private api: API;
  private readOnly: boolean;
  private config: any;
  private data: any;
  private block: BlockAPI;
  private hyperLinkNode: HTMLElement;

  constructor({ data, config, api, readOnly, block }: any) {
    this.api = api;
    this.readOnly = readOnly;
    this.config = config;
    this.block = block;
    this.data = { ...data, hyperLink: data.hyperLink || "" };
    this.hyperLinkNode = make(
      "div",
      [this.api.styles.input, "image-tool__hyperlink"],
      {
        contentEditable: !this.readOnly,
        "data-placeholder":
          this.config?.hyperLinkPlaceholder || "Enter hyperlink",
      }
    );

    // Initialize the original ImageTool
    this.imageTool = new ImageTool({
      data,
      config,
      api,
      readOnly,
      block: this.block,
    });
  }

  /**
   * Notify core that read-only mode is supported
   */
  static get isReadOnlySupported(): boolean {
    return true;
  }

  /**
   * Get Tool toolbox settings
   */
  static get toolbox() {
    return ImageTool.toolbox;
  }

  /**
   * Available image tunes
   */
  static get tunes() {
    return ImageTool.tunes;
  }

  /**
   * Specify paste substitutes
   */
  static get pasteConfig() {
    return ImageTool.pasteConfig;
  }

  /**
   * Enable line breaks
   */
  static get enableLineBreaks() {
    return true;
  }

  /**
   * Return Tool's UI
   */
  render() {
    const element = this.imageTool.render();
    if (this.config.hyperLink && this.config.caption) {
      element.classList.add("image-tool--hyperlink-caption");
      element.classList.remove("image-tool--caption");
    } else if (this.config.hyperLink) {
      element.classList.add("image-tool--hyperlink");
    }

    // Add your custom modifications here
    this.addCustomFeatures(element);

    return element;
  }

  /**
   * Add custom features to the image tool
   */
  private addCustomFeatures(element: HTMLElement) {
    this.hyperLinkNode.innerText = this.data.hyperLink || "";
    element.appendChild(this.hyperLinkNode);
  }

  /**
   * Render block tunes (settings)
   */
  renderSettings() {
    return this.imageTool.renderSettings();
  }

  /**
   * Validate data: check if Image exists
   */
  validate(savedData: any) {
    return this.imageTool.validate(savedData);
  }

  /**
   * Return Block data
   */
  save() {
    const savedData = this.imageTool.save();

    return {
      ...savedData,
      hyperLink: this.hyperLinkNode.innerText,
    };
  }

  /**
   * Fires after clicks on the Toolbox Image Icon
   */
  appendCallback() {
    this.imageTool.appendCallback();
  }

  /**
   * Specify paste handlers
   */
  async onPaste(event: PasteEvent): Promise<void> {
    return this.imageTool.onPaste(event);
  }
}

export default CustomImageTool;
